import React, { useCallback, useEffect, useState } from 'react';
import '../assets/scss/Containerfull.scss';
import MainNavbar from './MainNavbar';
import '../assets/scss/BrowserCompatibility.scss';
import MusicPlayer from './MusicPlayer';
import GuidedTour from './GuidedTour';
import ConversationUXGuard from './ConversationUXGuard';
import ConversationHintCoach from './ConversationHintCoach';
import MobileOffcanvasScrollGuard from './MobileOffcanvasScrollGuard';
import AssignmentShortcut from './AssignmentShortcut';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import useSpeakingFocusLock, { isSpeakingListeningFocus } from '../../hooks/useSpeakingFocusLock';
import { recordSpeakingAttention } from '../../services/learningActivityService';

const MINI_PLAYER_PATHS = [
    '/student/conversation',
    '/student/ai-generator',
    '/student/assignments',
    '/teacher/assignments'
];

const LEGACY_MINI_PLAYER_POSITION_KEY =
    'ae-mini-player-position-v1';

const Containerfull = ({ children }) => {
    const {
        playing,
        curr_margin
    } = useSelector(
        state => state.musicReducer
    );

    const location = useLocation();
    const { firebaseUser, role, authLoading } = useAuth();

    // 口說關卡的「先聽本關教材」頁：拿掉上方導覽列與下方選單，只留聽力，並鎖住站內跳頁。
    const speakingListeningFocus = isSpeakingListeningFocus(location);
    // 想離開聽力頁被帶回來，記一次口說專注度的 left_prep（只記學生，失敗不影響）。
    const recordLeftPrep = useCallback(lock => {
        const match = String(lock?.returnPath || '').match(/^\/student\/speaking-challenges\/(\d+)\?mode=(easy|challenge)/);
        if (!firebaseUser || !match) return;
        try { recordSpeakingAttention(firebaseUser, { reason: 'left_prep', question_set_id: Number(match[1]), mode: match[2] })?.catch?.(() => {}); } catch (error) { /* 記錄失敗不影響 */ }
    }, [firebaseUser]);
    useSpeakingFocusLock({ uid: firebaseUser?.uid || null, role, authLoading, onLeaveBlocked: recordLeftPrep });

    const [
        currMusic,
        setCurrMusic
    ] = useState(null);

    const [
        playerExpanded,
        setPlayerExpanded
    ] = useState(false);

    const miniPlayerPage =
        MINI_PLAYER_PATHS.some(
            pathname =>
                location.pathname === pathname ||
                location.pathname.startsWith(
                    `${pathname}/`
                )
        );

    const speakingChallengePage =
        location.pathname === '/student/speaking-challenges' ||
        location.pathname.startsWith(
            '/student/speaking-challenges/'
        );

    const showMiniPlayer = Boolean(
        currMusic &&
        miniPlayerPage &&
        !playerExpanded
    );

    // 口說挑戰期間保留曲目與播放位置，但完整封鎖教材播放器的
    // 顯示與操作，避免孩子在麥克風練習時分心或同時播放教材音檔。
    const hidePlayerForSpeaking = Boolean(
        currMusic && speakingChallengePage
    );

    // 作業頁展開後，「縮小」按鈕浮在播放器上方，內容要多留它的高度。
    const expandedToggleVisible = Boolean(
        currMusic &&
        !hidePlayerForSpeaking &&
        miniPlayerPage &&
        playerExpanded
    );

    const playerSpace =
        typeof curr_margin === 'number'
            ? `${curr_margin}px`
            : curr_margin || '110px';

    useEffect(() => {
        try {
            localStorage.removeItem(
                LEGACY_MINI_PLAYER_POSITION_KEY
            );
        } catch (error) {
            console.warn(
                '清除舊播放器位置失敗:',
                error
            );
        }
    }, []);

    useEffect(() => {
        const noInteractionCount =
            Number(
                localStorage.getItem(
                    'ae-no-interaction'
                )
            ) || 0;

        if (noInteractionCount >= 10) {
            setCurrMusic(null);
        } else {
            setCurrMusic(playing);
        }
    }, [playing]);

    useEffect(() => {
        setPlayerExpanded(false);
    }, [location.pathname]);

    return (
        <div
            className={`app-shell${speakingListeningFocus ? ' app-shell--focus' : ''}`}
            style={{
                '--app-player-space':
                    playerSpace
            }}
        >
            <MobileOffcanvasScrollGuard />

            {!speakingListeningFocus && (
                <header className="app-header">
                    <MainNavbar />
                </header>
            )}

            <main
                className={`app-content ${
                    currMusic && !hidePlayerForSpeaking
                        ? 'has-player'
                        : ''
                 } ${
                     showMiniPlayer
                         ? 'has-mini-player'
                         : ''
                 } ${
                     expandedToggleVisible
                         ? 'has-player-toggle'
                         : ''
                 }`}
                style={{
                    paddingBottom:
                        currMusic &&
                        !hidePlayerForSpeaking &&
                        !showMiniPlayer
                            ? expandedToggleVisible
                                ? `calc(${playerSpace} + 52px)`
                                : curr_margin ||
                                  '110px'
                            : undefined
                }}
            >
                {children}
            </main>

            {!speakingListeningFocus && <AssignmentShortcut
                playerVisible={
                    Boolean(currMusic) &&
                    !hidePlayerForSpeaking
                }
                compactPlayer={
                    showMiniPlayer
                }
            />}

            <ConversationUXGuard />
            <ConversationHintCoach />
            <GuidedTour />

            {currMusic && (
                <footer
                    className={`app-player ${
                        showMiniPlayer
                            ? 'mini'
                            : ''
                    } ${
                        hidePlayerForSpeaking
                            ? 'speaking-player-blocked'
                            : ''
                    }`}
                    aria-label="音樂播放器"
                    aria-hidden={hidePlayerForSpeaking}
                    hidden={hidePlayerForSpeaking}
                    data-speaking-player-hidden={
                        hidePlayerForSpeaking
                            ? 'true'
                            : undefined
                    }
                >
                    {miniPlayerPage && (
                        <button
                            type="button"
                            className="app-player-toggle"
                            onClick={() =>
                                setPlayerExpanded(
                                    previous =>
                                        !previous
                                )
                            }
                            aria-expanded={
                                playerExpanded
                            }
                            aria-label={
                                playerExpanded
                                    ? '縮小播放器'
                                    : '展開播放器'
                            }
                        >
                            <span
                                aria-hidden="true"
                            >
                                🎧
                            </span>

                            {playerExpanded
                                ? '縮小'
                                : '展開播放器'}
                        </button>
                    )}

                    <MusicPlayer
                        music={currMusic}
                        pausePlayback={hidePlayerForSpeaking}
                    />
                </footer>
            )}
        </div>
    );
};

export default Containerfull;
